import React, { useCallback, useEffect, useRef, useState } from "react";
import CassetteTape from "./CassetteTape";
import VUMeter from "./VUMeter";
import "./styles/CassetteTapeDeckPlayer.css";

interface CassetteTapeDeckPlayerProps {
  audioUrl: string;
  onEject: () => void;
}

const CassetteDeckPlayer = ({
  audioUrl,
  onEject,
}: CassetteTapeDeckPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const clickAudioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isRewinding, setIsRewinding] = useState(false);
  const [isFastForwarding, setIsFastForwarding] = useState(false);
  const [isFullRewinding, setIsFullRewinding] = useState(false);

  // Control buttons
  const [playButton, setPlayButton] = useState(false);
  const [rewindButton, setRewindButton] = useState(false);
  const [forwardButton, setForwardButton] = useState(false);
  const [pauseButton, setPauseButton] = useState(false);
  const [fullRewindButton, setFullRewindButton] = useState(false);

  const [hasEnded, setHasEnded] = useState(false);
  const fullRewindIntervalRef = useRef<number | null>(null);

  const playClickSound = useCallback(() => {
    if (clickAudioRef.current) {
      clickAudioRef.current.currentTime = 0; // Reset to start
      clickAudioRef.current.play().catch(() => {
        // Ignore errors if audio can't play
      });
    }
  }, []);

  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.volume = volume;
    }
  }, [audioUrl, volume]);

  // Update time every 250ms
  useEffect(() => {
    let raf: number;
    const update = () => {
      if (audioRef.current) {
        const currentTime = audioRef.current.currentTime;
        const duration = audioRef.current.duration;

        setTime(currentTime);

        // Check if audio has ended
        if (duration && currentTime >= duration && playing) {
          setPlaying(false);
          setHasEnded(true);
        } else if (currentTime > 0 && currentTime < duration) {
          setHasEnded(false);
        }
      }
      raf = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(raf);
  }, [playing, hasEnded]);

  const formatTime = (t: number) => {
    const mins = Math.floor(t / 60);
    const secs = Math.floor(t % 60)
      .toString()
      .padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const stopFullRewind = useCallback(() => {
    if (fullRewindIntervalRef.current) {
      clearInterval(fullRewindIntervalRef.current);
      fullRewindIntervalRef.current = null;
    }
    setIsFullRewinding(false);
    setIsRewinding(false);
  }, []);

  const play = useCallback(() => {
    playClickSound();
    stopFullRewind();
    setHasEnded(false);

    audioRef.current?.play();

    setPlaying(true);
    setIsFastForwarding(false);
    setPlayButton(true);
    setPauseButton(false);
  }, [stopFullRewind, playClickSound]);

  const pause = useCallback(() => {
    playClickSound();
    stopFullRewind();

    audioRef.current?.pause();

    setPlaying(false);
    setIsFastForwarding(false);

    setPauseButton(true);
    setPlayButton(false);
    setFullRewindButton(false);
  }, [stopFullRewind, playClickSound]);

  const rewind = useCallback(() => {
    playClickSound();

    if (audioRef.current) {
      stopFullRewind();

      const currentTime = audioRef.current.currentTime;
      const newTime = Math.max(0, currentTime - 5);
      const wasPlaying = playing;

      audioRef.current.pause();
      setPlaying(false);

      setRewindButton(true);
      setFullRewindButton(false);

      audioRef.current.currentTime = newTime;

      if (currentTime > 0 && newTime < currentTime) {
        setIsRewinding(true);
        setIsFastForwarding(false);

        setTimeout(() => {
          setIsRewinding(false);

          setRewindButton(false);

          // Resume playing after animation delay if it was playing before
          if (wasPlaying) {
            setTimeout(() => {
              audioRef.current?.play();
              setPlaying(true);
            }, 200);
          }
        }, 600);
      }
    }
  }, [playing, stopFullRewind, playClickSound]);

  const forward = useCallback(() => {
    playClickSound();

    if (audioRef.current) {
      stopFullRewind();

      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      const newTime = Math.min(duration, currentTime + 5);
      const wasPlaying = playing;

      audioRef.current.pause();
      setPlaying(false);

      setForwardButton(true);
      setFullRewindButton(false);

      audioRef.current.currentTime = newTime;

      if (currentTime < duration && newTime > currentTime) {
        setIsFastForwarding(true);
        setIsFullRewinding(false);

        setTimeout(() => {
          setIsFastForwarding(false);

          setForwardButton(false);

          // Resume playing after animation delay if it was playing before
          if (wasPlaying) {
            setTimeout(() => {
              audioRef.current?.play();
              setPlaying(true);
            }, 200);
          }
        }, 600);
      }
    }
  }, [playing, stopFullRewind, playClickSound]);

  const fullRewind = useCallback(() => {
    playClickSound();

    if (audioRef.current) {
      if (isFullRewinding) {
        // Stop full rewind
        stopFullRewind();
      } else {
        // Start full rewind
        const wasPlaying = playing;

        // Pause audio immediately
        audioRef.current.pause();
        setPlaying(false);
        setIsFullRewinding(true);
        setIsRewinding(true);
        setIsFastForwarding(false);

        setFullRewindButton(true);
        setPlayButton(false);
        setRewindButton(false);
        setForwardButton(false);
        setPauseButton(false);

        // Start rewinding in 5-second steps every second
        fullRewindIntervalRef.current = window.setInterval(() => {
          if (audioRef.current) {
            const currentTime = audioRef.current.currentTime;
            const newTime = Math.max(0, currentTime - 5);
            audioRef.current.currentTime = newTime;

            // If we've reached the beginning, stop rewinding
            if (newTime <= 0) {
              if (fullRewindIntervalRef.current) {
                clearInterval(fullRewindIntervalRef.current);
                fullRewindIntervalRef.current = null;
              }
              setIsFullRewinding(false);
              setIsRewinding(false);

              setFullRewindButton(false);

              // Resume playing if it was playing before, with delay
              if (wasPlaying) {
                setTimeout(() => {
                  audioRef.current?.play();
                  setPlaying(true);
                }, 200);
              }
            }
          }
        }, 1000);
      }
    }
  }, [playing, isFullRewinding, stopFullRewind, playClickSound]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (fullRewindIntervalRef.current) {
        clearInterval(fullRewindIntervalRef.current);
      }
    };
  }, []);

  const onVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  if (audioUrl) {
    return (
      <div className="cassette-deck-player">
        <div className="cassette-deck-player-elements">
          <div className="deck-eject-elements">
            <button className="deck-eject" onClick={onEject}>
              ⏏
            </button>
            <span>EJECT AND RETURN</span>
          </div>
          <div className="deck-window">
            <div className="tape-window">
              <CassetteTape
                isPlaying={playing}
                isRewinding={isRewinding || isFullRewinding}
                isFastForwarding={isFastForwarding}
              />
            </div>
          </div>
          <div className="deck-playback">
            <div className="vu-meter-container">
              <VUMeter audioElement={audioRef.current} sensitivity={1.5} />
            </div>

            <div className="deck-controls">
              <div className="deck-time-display">{formatTime(time)}</div>

              <div className="deck-control-buttons">
                <button
                  className={`${playButton ? "active" : ""}`}
                  onClick={play}
                >
                  ▶
                </button>
                <button
                  className={`${rewindButton ? "active" : ""}`}
                  onClick={rewind}
                >
                  ⏴⏴
                </button>
                <button
                  className={`${forwardButton ? "active" : ""}`}
                  onClick={forward}
                >
                  ⏵⏵
                </button>
                <button
                  className={`${pauseButton ? "active" : ""}`}
                  onClick={pause}
                >
                  ⏸
                </button>
                <button
                  className={`${fullRewindButton ? "active" : ""}`}
                  onClick={fullRewind}
                >
                  ⏮
                </button>
              </div>
            </div>
          </div>
          <div className="deck-volume-control">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={onVolumeChange}
            />
          </div>
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            crossOrigin="anonymous"
          />
        </div>
        <audio
          ref={clickAudioRef}
          src="/assets/themes/tape/audio/press.mp3"
          preload="auto"
        />
      </div>
    );
  } else {
    return <></>;
  }
};

export default CassetteDeckPlayer;
